import { type ApolloError, useMutation } from '@apollo/client';
import { datadogLogs } from '@datadog/browser-logs';
import {
  type CreateScheduledDepositTransferInput,
  FeatureFlagTypes,
  Frequency,
  type WorkflowCompletion,
} from '@generated/graphql';
import { WorkflowCompletionForm } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionForm';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import {
  type CustodianDepositTransferContextInput,
  toTransferContextData,
  type WorkflowCustodianDepositTransfer,
} from '@ovComponents/4-module/workflowCompletion/workflowTypes';
import { useInactivityTimeout } from '@providers/inActivityTimeoutProvider';
import { UserContext } from '@providers/userContextProvider';
import { getGraphQLErrorMessage } from '@utils/graphQLError';
import { updateContextTransfer } from '@utils/updateWorkflowContext';
import { Box, Button, Typography, useToast } from 'hephaestus/web';
import { Loop } from 'hephaestus/webIcons';
import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useLocalization } from '../../../../../hooks/useLocalization';
import { useOrganizationFeatureFlag } from '../../../../../hooks/useOrganizationFeatureFlag';
import { GO_TO_STEP } from '../queries';
import { useDepositWorkflowData } from '../utils/useDepositWorkflowData';
import {
  CREATE_CUSTODIAN_TRANSFER,
  CREATE_DEPOSIT_TRANSFER,
  CREATE_EXTERNAL_TRANSFER,
  CREATE_SCHEDULED_DEPOSIT_TRANSFER,
  TRANSFER_ALL_BETWEEN_SUB_ACCOUNTS,
  TRANSFER_BETWEEN_SUB_ACCOUNTS,
  UPDATE_SCHEDULED_TRANSFER,
} from './depositReview.queries';
import DepositReviewVisual, {
  type DepositReviewSubstepOptions,
} from './depositReview.visual';
import {
  applySubmissionResults,
  findAddAnotherDepositTargetSubStep,
  isFullInternalTransfer,
  resolveDepositReviewActions,
  resolveDepositReviewSubmitLabelKey,
} from './depositReviewRules';

type HeaderContextArgs = {
  sessionId?: string;
};

type CustodianDepositTransferResponse = {
  status: string;
  confirmationNumber: string;
  action?: {
    type: string;
    details?: { key: string; value: unknown }[];
  } | null;
};

type WorkflowCompletionStatus =
  | 'success'
  | 'failure'
  | 'error'
  | 'deny'
  | 'silent';

export const DepositReview = ({
  options,
  onNext,
  dummyDeposit,
  setDummyDeposit,
  workflowCompletion,
  setActiveWorkflowCompletion,
}: {
  options: DepositReviewSubstepOptions;
  onNext: any;
  dummyDeposit?: any;
  setDummyDeposit?: any;
  workflowCompletion?: WorkflowCompletion;
  setActiveWorkflowCompletion?: any;
}) => {
  const { t } = useTranslation('workflowCompletions');
  const { translateBackendString } = useLocalization();
  const { userContext } = useContext(UserContext);
  const { workflowData, setWorkflowData } = useContext(WorkflowContext);
  const { custodianConnection } = useContext(UserContext);
  const {
    applicableData,
    setApplicationContext,
    setWorkflowCompletionContext,
    updateContextWithFields,
    mode,
  } = useDepositWorkflowData(dummyDeposit, setDummyDeposit, workflowCompletion);
  const [loading, setLoading] = useState<boolean>(false);
  const { enqueueToast } = useToast();
  const usTransferFlowEnabled = useOrganizationFeatureFlag(
    FeatureFlagTypes.UsTransferFlow,
  );
  const enableMultiCurrencyDepositTransfers = useOrganizationFeatureFlag(
    FeatureFlagTypes.MultiCurrencyDepositTransfers,
  );
  const padAgreementsEnabled = useOrganizationFeatureFlag(
    FeatureFlagTypes.PadAgreements,
  );
  const { attemptSilentRefresh } = useInactivityTimeout();

  // Add new states for workflow completion control
  const custodianActionTakenRef = useRef(false);
  // Refs for immediate access to prevent race conditions with React's async state updates
  const workflowCompletedRef = useRef(false);
  const hasErrorRef = useRef(false);

  const recordWorkflowTransfers = useCallback(
    async (transfer: { id: string }) => {
      await setWorkflowData((data) => ({
        ...data,
        currentTransferId: transfer.id,
        createdTransfers: [...(data?.createdTransfers ?? []), transfer],
      }));
    },
    [setWorkflowData],
  );
  const recordWorkflowScheduledTransfers = useCallback(
    async (scheduledTransfer: { id: string }) => {
      await setWorkflowData((data) => ({
        ...data,
        createdScheduledTransfers: [
          ...(data?.createdScheduledTransfers ?? []),
          scheduledTransfer,
        ],
      }));
    },
    [setWorkflowData],
  );

  type ManualProcess = NonNullable<
    (typeof workflowData)['createdExternalTransfers']
  >[number];
  const recordWorkflowExternalTransfer = useCallback(
    async (transfer: ManualProcess) => {
      await setWorkflowData((data) => ({
        ...data,
        createdExternalTransfers: [
          ...(data?.createdExternalTransfers ?? []),
          transfer,
        ],
      }));
    },
    [setWorkflowData],
  );
  const recordWorkflowCustodianDepositTransfer = useCallback(
    async (transfer: WorkflowCustodianDepositTransfer) => {
      await setWorkflowData((data) => ({
        ...data,
        createdCustodianDepositTransfers: [
          ...(data?.createdCustodianDepositTransfers ?? []),
          transfer,
        ],
      }));
    },
    [setWorkflowData],
  );

  const handleWorkflowCompletion = useCallback(
    async (status: WorkflowCompletionStatus = 'success') => {
      if (workflowCompletedRef.current) return; // Prevent double completion

      workflowCompletedRef.current = true; // Immediate access for race condition prevention
      await onNext();
      setLoading(false);

      // Only show toast for non-silent completions
      switch (status) {
        case 'success':
          enqueueToast(t('depositReview.success'), { variant: 'success' });
          break;
        case 'failure':
          enqueueToast(t('depositReview.failure'), { variant: 'error' });
          break;
        case 'deny':
          enqueueToast(t('depositReview.deny'), { variant: 'error' });
          break;
        case 'error':
          enqueueToast(t('depositReview.error'), { variant: 'error' });
          break;
        case 'silent':
          // Complete workflow without showing any toast
          // Useful when you want to complete the workflow without showing a toast
          // E.g. when redirecting to external apps like a risk challenge app for ATB
          break;
      }
    },
    [onNext, enqueueToast, t],
  );

  const freshTransferContextData = useMemo(
    () => ({
      ...toTransferContextData({}),
      skipDocuments: applicableData.skipDocuments,
      currentDepositTemporaryId: undefined,
    }),
    [applicableData.skipDocuments],
  );

  // TODO (OVT-6217): This helper is unreachable after the Simility removal —
  // `cmid` is always null and the custodian no longer emits `action: CHALLENGE`,
  // so this code is dormant until the replacement fraud engine is wired up. At
  // that point, we can do any needed refactor with applicable context/details.
  const handleCustodianRiskEngineChallenge = async (
    cmid: string | null,
    data: CustodianDepositTransferResponse,
  ) => {
    const { action } = data;
    const challengedTransfersEnabled =
      custodianConnection?.enableChallengedCustodianDepositTransfer === true;
    const challengeURL = custodianConnection?.metadata?.find(
      (m) => m.key === 'challengeUrl',
    )?.value as string;
    const transactionId = action?.details?.find(
      (d) => d.key === 'transactionId',
    )?.value as string;
    if (!custodianConnection?.enableChallengedCustodianDepositTransfer) {
      datadogLogs.logger.warn(
        'Custodian Deposit Transfer - enableChallengedCustodianDepositTransfer is not set. Cannot proceed to challenge',
      );
      await handleWorkflowCompletion('error');
      return;
    }

    if (!challengeURL) {
      datadogLogs.logger.warn(
        'Custodian Deposit Transfer - custodian connection does not have challengeUrl. Cannot proceed to challenge',
      );
      await handleWorkflowCompletion('error');
      return;
    }

    if (!transactionId) {
      datadogLogs.logger.warn(
        'Custodian Deposit Transfer - No transactionId was returned. Cannot proceed to challenge',
      );
      await handleWorkflowCompletion('error');
      return;
    }

    if (action && challengedTransfersEnabled && challengeURL && transactionId) {
      if (!cmid) {
        datadogLogs.logger.warn(
          'Custodian Deposit Transfer - CMID is not set. Cannot proceed to challenge',
        );
        await handleWorkflowCompletion('error');
        return;
      }

      // Refresh access token to reset its validity window
      if (attemptSilentRefresh) {
        attemptSilentRefresh();
      }

      const { protocol, host, pathname } = window.location;
      const inboundRedirectUrl = `${protocol}//${host}${pathname}?atbmeta=transactionId:${transactionId}`;
      const toUrlEncode = `session-id=${cmid}&transaction-id=${transactionId}&redirect=${inboundRedirectUrl}`;
      const outboundRedirectUrl = `${challengeURL}?${encodeURIComponent(toUrlEncode)}`;

      try {
        datadogLogs.logger.info(
          'Custodian Deposit Transfer - will redirect to custodian risk challenge',
          { cmid, transactionId },
        );

        // Complete workflow first (this closes the workflow dialog)
        // then redirect in quick succession
        await handleWorkflowCompletion('silent');
        window.localStorage.setItem(
          'custodianRiskChallengeTransaction',
          transactionId,
        );
        window.location.replace(outboundRedirectUrl);
      } catch (error) {
        datadogLogs.logger.error(
          'Custodian Deposit Transfer - error redirecting to custodian risk challenge',
          { data, error },
        );
        enqueueToast(t('unableToRedirectForVerification'), {
          variant: 'error',
        });
      }
    }
  };

  const handleCustodianRiskEngineActionIfAny = async (
    cmid: string | null,
    data: CustodianDepositTransferResponse,
  ) => {
    const { action } = data || {};
    if (action && action.type) {
      const actionType = action.type.trim().toUpperCase();
      switch (actionType) {
        case 'DENY':
          datadogLogs.logger.info('Custodian Deposit Transfer - DENY', {
            actionType,
            cmid,
          });
          await handleWorkflowCompletion('deny');
          break;
        // TODO (OVT-6217): Unreachable after the Simility removal — the custodian
        // no longer emits `action: CHALLENGE`, so this branch is dormant until the
        // replacement fraud engine is wired up. At that point, we can do any
        // needed refactor with applicable context/details.
        case 'CHALLENGE':
          datadogLogs.logger.info('Custodian Deposit Transfer - CHALLENGE', {
            actionType,
            cmid,
          });
          await handleCustodianRiskEngineChallenge(cmid, data);
          break;
        default:
          datadogLogs.logger.info(
            'Custodian Deposit Transfer - UNKNOWN ACTION',
            { actionType, cmid },
          );
          // For unknown actions, allow the standard workflow
          // completion (i.e success) to take place
          custodianActionTakenRef.current = false;
          break;
      }
    }
  };

  // TODO (OVT-6217): `CMID` is always null after the Simility removal — no
  // code path writes it to sessionStorage, so every downstream branch gated on
  // it in this file is a no-op until the replacement fraud engine is wired up.
  // At that point, we can do any needed refactor with applicable context/details.
  const CMID = window.sessionStorage.getItem('CMID');
  const buildHeaderContext = ({ sessionId }: HeaderContextArgs = {}) =>
    sessionId && typeof sessionId === 'string' && sessionId.trim().length === 32
      ? { headers: { 'session-id': sessionId } }
      : {};

  const [createDepositTransfer] = useMutation<{
    createDepositTransfer: { transfer: { id: string } };
  }>(CREATE_DEPOSIT_TRANSFER);
  const [createScheduledDepositTransferMutation] = useMutation(
    CREATE_SCHEDULED_DEPOSIT_TRANSFER,
  );
  const createScheduledDepositTransfer: typeof createScheduledDepositTransferMutation =
    async (...args) => {
      const result = await createScheduledDepositTransferMutation(...args);
      const skipDocuments = !padAgreementsEnabled;
      await updateContextTransfer({
        value: { ...applicableData, skipDocuments },
        existingContext: applicableData,
        setApplicationContext,
        setWorkflowCompletionContext,
      });
      return result;
    };
  const [createTransferBetweenSubAccounts] = useMutation<{
    transferBetweenSubAccounts: { transfer: { id: string } };
  }>(TRANSFER_BETWEEN_SUB_ACCOUNTS);
  const [createTransferAllBetweenSubAccounts] = useMutation<{
    transferAllBetweenSubAccounts: { transfer: { id: string } };
  }>(TRANSFER_ALL_BETWEEN_SUB_ACCOUNTS);
  const [createExternalTransfer] = useMutation<{
    createExternalTransfer: { manualProcess: ManualProcess };
  }>(CREATE_EXTERNAL_TRANSFER);
  const [createCustodianTransfer] = useMutation<{
    createCustodianDepositTransfer: CustodianDepositTransferResponse;
  }>(CREATE_CUSTODIAN_TRANSFER, {
    onError: () => {
      hasErrorRef.current = true;
      handleWorkflowCompletion('failure');
    },
    onCompleted(data) {
      const hasAction =
        data.createCustodianDepositTransfer.action != null &&
        typeof data.createCustodianDepositTransfer.action === 'object';
      if (hasAction) {
        custodianActionTakenRef.current = true;
        handleCustodianRiskEngineActionIfAny(
          CMID,
          data.createCustodianDepositTransfer,
        );
      }
    },
  });

  const [updateScheduledTransferMutation] = useMutation(
    UPDATE_SCHEDULED_TRANSFER,
  );
  const updateScheduledTransfer: typeof updateScheduledTransferMutation =
    async (...args) => {
      const result = await updateScheduledTransferMutation(...args);
      const skipDocuments = !padAgreementsEnabled;
      await updateContextTransfer({
        value: { ...applicableData, skipDocuments },
        existingContext: applicableData,
        setApplicationContext,
        setWorkflowCompletionContext,
      });
      return result;
    };

  const [goToStep] = useMutation(GO_TO_STEP());
  const toast = useToast();

  const { canAddAnother } = resolveDepositReviewActions(options);

  const submit = async () => {
    setLoading(true);
    custodianActionTakenRef.current = false;
    hasErrorRef.current = false;

    // Deferred external deposit transfer: save input to context and advance
    // to the SynchronousIntegration step. The Windmill script will handle
    // the actual custodian deposit transfer API call.
    //
    // Multiple deposits are not supported because each custodian challenge
    // redirects the user externally for MFA, which cannot be serialized
    // across multiple deposits in a single workflow session.
    //
    // See: https://onevest.getoutline.com/doc/business-case-workflow-engine-interactivity-dynamic-response-handling-JeG4OzzXDZ
    if (
      options.deferredExternalDepositTransfer &&
      options.createExternalDepositTransfer
    ) {
      if (!CMID) {
        datadogLogs.logger.warn('Custodian deposit transfer: CMID is missing');
      }

      try {
        const deposit = applicableData;
        const scheduledDate = deposit?.scheduledDate;

        // Create the internal OV deposit record if the org has opted in.
        if (
          custodianConnection?.enableSaveInternalRecordsOfCustodianDepositTransfers ===
          true
        ) {
          const { data } = await createDepositTransfer({
            variables: {
              input: {
                amountCents: deposit?.amountCents,
                externalBankAccountId: deposit?.bankAccount?.id,
                subAccountId: deposit?.subAccount?.id,
                fiscalYear: deposit?.fiscalYear,
                ...(enableMultiCurrencyDepositTransfers
                  ? { currency: deposit?.bankAccount?.currency }
                  : {}),
              },
            },
          });
          if (data?.createDepositTransfer?.transfer) {
            await recordWorkflowTransfers(data.createDepositTransfer.transfer);
          }
        }

        const custodianDepositTransferInput: CustodianDepositTransferContextInput =
          {
            cmid: CMID || undefined,
            accountId: deposit?.subAccount?.account?.id,
            organizationUserId: userContext?.id,
            amount: deposit?.amountCents,
            fund: deposit?.holdings?.financialProduct?.cusip ?? undefined,
            frequency: deposit?.schedule,
            startDate:
              scheduledDate instanceof Date
                ? scheduledDate.toISOString()
                : scheduledDate,
            bankAccount: {
              accountNumber: deposit?.bankAccount?.bankAccountNumberDisclosed,
              institutionNumber: deposit?.bankAccount?.institutionNumber,
              transitNumber: deposit?.bankAccount?.transitNumberDisclosed,
            },
          };

        await setWorkflowData((prev) => ({
          ...prev,
          custodianDepositTransfer: { input: custodianDepositTransferInput },
        }));

        await onNext();
        // Intentionally do NOT reset loading. The component unmounts
        // when the workflow advances, and keeping the button disabled
        // prevents double-click race conditions.
      } catch (e: unknown) {
        const errorMessage = e instanceof Error ? e.message : undefined;
        datadogLogs.logger.error(
          'Deferred custodian deposit transfer: failed to save transfer input to context',
          { error: e },
        );
        enqueueToast(errorMessage || t('depositReview.error'), {
          variant: 'error',
        });
        setLoading(false);
      }
      return;
    }

    const temporaryDeposits = applicableData.temporaryDeposits ?? [];

    const failedTemporaryDeposits = [] as Array<{
      deposit: typeof applicableData;
      err: Error;
    }>;
    const referenceIds: Record<string, string | undefined> = {};
    const depositsToSubmit = temporaryDeposits?.filter(
      (deposit) => deposit.temporaryTransferState !== 'completed',
    );

    if (depositsToSubmit.length === 0) {
      // Early return if no deposits to submit - complete workflow silently without showing success toast
      await updateContextWithFields(freshTransferContextData);
      await handleWorkflowCompletion('silent');
      setLoading(false);
      return;
    }

    for (const deposit of depositsToSubmit) {
      const { temporaryId } = deposit;
      try {
        if (mode === 'edit') {
          await updateScheduledTransfer({
            variables: {
              input: {
                scheduledTransferId: deposit?.scheduledTransferId ?? '',
                scheduledDate: deposit?.scheduledDate as string,
                frequency: deposit?.schedule as Frequency,
                amountCents: deposit?.amountCents,
                bankAccountId: deposit?.bankAccount?.id,
              },
            },
          });
        } else {
          if (deposit?.type === 'EFT') {
            if (deposit?.schedule === 'ONE_TIME') {
              // non scheduled
              if (deposit?.scheduledDate) {
                const input = {
                  frequency: Frequency.OneTime,
                  amountCents: deposit?.amountCents || 0,
                  bankAccountId: !options.createExternalDepositTransfer
                    ? deposit?.bankAccount?.id
                    : undefined,
                  externalBankAccountId: options.createExternalDepositTransfer
                    ? deposit?.bankAccount?.id
                    : undefined,
                  subAccountId: deposit?.subAccount?.id as string,
                  scheduledDate: deposit?.scheduledDate as string,
                  fiscalYear: deposit?.fiscalYear ?? undefined,
                  ...(enableMultiCurrencyDepositTransfers &&
                  deposit?.bankAccount?.currency
                    ? { currency: deposit?.bankAccount?.currency }
                    : {}),
                } as CreateScheduledDepositTransferInput;

                const { data } = await createScheduledDepositTransfer({
                  variables: { input },
                });

                if (data?.createScheduledDepositTransfer?.scheduledTransfer) {
                  const {
                    createScheduledDepositTransfer: { scheduledTransfer },
                  } = data;
                  if (temporaryId)
                    referenceIds[temporaryId] = scheduledTransfer.id;
                  await recordWorkflowScheduledTransfers(scheduledTransfer);
                }
              } else {
                if (
                  !options.createExternalDepositTransfer ||
                  (options.createExternalDepositTransfer &&
                    custodianConnection?.enableSaveInternalRecordsOfCustodianDepositTransfers ===
                      true)
                ) {
                  const input = {
                    amountCents: deposit?.amountCents,
                    bankAccountId: !options.createExternalDepositTransfer
                      ? deposit?.bankAccount?.id
                      : undefined,
                    externalBankAccountId: options.createExternalDepositTransfer
                      ? deposit?.bankAccount?.id
                      : undefined,
                    subAccountId: deposit?.subAccount?.id,
                    fiscalYear: deposit?.fiscalYear,
                    ...(enableMultiCurrencyDepositTransfers
                      ? { currency: deposit?.bankAccount?.currency }
                      : {}),
                  };

                  const { data } = await createDepositTransfer({
                    variables: { input },
                  });
                  if (data?.createDepositTransfer?.transfer) {
                    const {
                      createDepositTransfer: { transfer },
                    } = data;
                    if (temporaryId) referenceIds[temporaryId] = transfer.id;
                    await recordWorkflowTransfers(transfer);
                  }
                }

                if (options?.createExternalDepositTransfer) {
                  const input = {
                    accountId: deposit?.subAccount?.account?.id,
                    amount: deposit?.amountCents,
                    fund: deposit?.holdings?.financialProduct?.cusip,
                    frequency: deposit?.schedule,
                    startDate: deposit?.scheduledDate,
                    bankAccount: {
                      accountNumber:
                        deposit?.bankAccount?.bankAccountNumberDisclosed,
                      institutionNumber:
                        deposit?.bankAccount?.institutionNumber,
                      transitNumber:
                        deposit?.bankAccount?.transitNumberDisclosed,
                    },
                  };
                  const { data } = await createCustodianTransfer({
                    variables: { input },
                    ...(CMID && {
                      context: {
                        ...buildHeaderContext({ sessionId: CMID }),
                      },
                    }),
                  });
                  if (data?.createCustodianDepositTransfer) {
                    const {
                      createCustodianDepositTransfer: custodianDepositTransfer,
                    } = data;
                    if (temporaryId)
                      referenceIds[temporaryId] =
                        custodianDepositTransfer.confirmationNumber;

                    await recordWorkflowCustodianDepositTransfer({
                      ...input,
                      confirmationNumber:
                        custodianDepositTransfer.confirmationNumber,
                    });
                  } else {
                    datadogLogs.logger.error(
                      'Custodian deposit transfer returned no data',
                      { input },
                    );
                  }
                }
              }
            } else {
              // scheduled
              if (
                !options.createExternalDepositTransfer ||
                (options.createExternalDepositTransfer &&
                  custodianConnection?.enableSaveInternalRecordsOfCustodianDepositTransfers ===
                    true)
              ) {
                const input = {
                  amountCents: deposit?.amountCents,
                  bankAccountId: !options.createExternalDepositTransfer
                    ? deposit?.bankAccount?.id
                    : undefined,
                  externalBankAccountId: options.createExternalDepositTransfer
                    ? deposit?.bankAccount?.id
                    : undefined,
                  subAccountId: deposit?.subAccount?.id,
                  scheduledDate: deposit?.scheduledDate,
                  frequency: deposit?.schedule,
                  fiscalYear: deposit?.fiscalYear ?? undefined,
                  ...(enableMultiCurrencyDepositTransfers
                    ? { currency: deposit?.bankAccount?.currency }
                    : {}),
                } as CreateScheduledDepositTransferInput;

                const { data } = await createScheduledDepositTransfer({
                  variables: { input },
                });
                if (data?.createScheduledDepositTransfer?.scheduledTransfer) {
                  const {
                    createScheduledDepositTransfer: { scheduledTransfer },
                  } = data;
                  if (temporaryId)
                    referenceIds[temporaryId] = scheduledTransfer.id;
                  await recordWorkflowScheduledTransfers(scheduledTransfer);
                }
              }

              if (options?.createExternalDepositTransfer) {
                const input = {
                  accountId: deposit?.subAccount?.account?.id,
                  amount: deposit?.amountCents,
                  fund: deposit?.holdings?.financialProduct?.cusip,
                  frequency: deposit?.schedule,
                  startDate: deposit?.scheduledDate,
                  bankAccount: {
                    accountNumber:
                      deposit?.bankAccount?.bankAccountNumberDisclosed,
                    institutionNumber: deposit?.bankAccount?.institutionNumber,
                    transitNumber: deposit?.bankAccount?.transitNumberDisclosed,
                  },
                };
                const { data } = await createCustodianTransfer({
                  variables: { input },
                  ...(CMID && {
                    context: {
                      ...buildHeaderContext({ sessionId: CMID }),
                    },
                  }),
                });
                if (data?.createCustodianDepositTransfer) {
                  const { createCustodianDepositTransfer } = data;
                  await recordWorkflowCustodianDepositTransfer({
                    ...input,
                    confirmationNumber:
                      createCustodianDepositTransfer.confirmationNumber,
                  });
                } else {
                  datadogLogs.logger.error(
                    'Custodian Scheduled deposit transfer returned no data',
                    { input },
                  );
                }
              }
            }
          } else if (deposit?.type === 'INTERNAL_TRANSFER') {
            if (!isFullInternalTransfer(deposit)) {
              const input = {
                amountCents: deposit?.amountCents,
                fromSubAccountId: deposit?.fromSubAccount?.id,
                toSubAccountId: deposit?.subAccount?.id,
                fiscalYear: deposit?.fiscalYear ?? undefined,
                ...(enableMultiCurrencyDepositTransfers && deposit?.currency
                  ? {
                      currency: deposit?.currency,
                    }
                  : {}),
              };

              const { data } = await createTransferBetweenSubAccounts({
                variables: { input },
              });
              if (data?.transferBetweenSubAccounts?.transfer) {
                const {
                  transferBetweenSubAccounts: { transfer },
                } = data;
                await recordWorkflowTransfers(transfer);
              }
            } else {
              const input = {
                fromSubAccountId: deposit?.fromSubAccount?.id,
                toSubAccountId: deposit?.subAccount?.id,
                fiscalYear: deposit?.fiscalYear ?? undefined,
                ...(enableMultiCurrencyDepositTransfers && deposit?.currency
                  ? {
                      currency: deposit?.currency,
                    }
                  : {}),
              };

              const { data } = await createTransferAllBetweenSubAccounts({
                variables: { input },
              });
              if (data?.transferAllBetweenSubAccounts?.transfer) {
                const {
                  transferAllBetweenSubAccounts: { transfer },
                } = data;
                if (temporaryId) referenceIds[temporaryId] = transfer.id;
                await recordWorkflowTransfers(transfer);
              }
            }
          } else {
            const input = {
              amountCents: deposit?.amountCents,
              subAccountId: deposit?.subAccount?.id,
              institution: { id: deposit?.institution?.id },
              transferAccountNumber: deposit?.accountNumber,
              transferMethod: deposit?.transferType,
              transferAccount: deposit?.accountType,
              participantNumber:
                usTransferFlowEnabled &&
                deposit.subAccount?.account?.type?.includes('USA_') &&
                deposit.participantNumber
                  ? deposit.participantNumber
                  : undefined,
            };

            const { data } = await createExternalTransfer({
              variables: { input },
            });
            if (data?.createExternalTransfer?.manualProcess?.id) {
              const {
                createExternalTransfer: { manualProcess },
              } = data;
              if (temporaryId) referenceIds[temporaryId] = manualProcess.id;
              await recordWorkflowExternalTransfer(manualProcess);
            }
          }
        }
      } catch (err) {
        failedTemporaryDeposits.push({ deposit, err: err as Error });
      }
    }

    const failedTemporaryIds = failedTemporaryDeposits.map(
      ({ deposit }) => deposit.temporaryId ?? '',
    );
    const currentDepositSucceeded = !failedTemporaryIds.includes(
      applicableData.currentDepositTemporaryId ?? '',
    );

    await updateContextWithFields({
      ...(currentDepositSucceeded ? freshTransferContextData : {}),
      temporaryDeposits: applySubmissionResults({
        deposits: temporaryDeposits,
        failedTemporaryIds,
        referenceIds,
      }),
    });
    if (failedTemporaryDeposits.length) {
      setLoading(false);
      datadogLogs.logger.warn(
        'multi deposit review could not complete all deposits',
        {
          workflowCompletion,
          applicableData,
        },
      );
      const validationMessage = failedTemporaryDeposits
        .map(({ err }) =>
          getGraphQLErrorMessage(
            translateBackendString,
            (err as ApolloError).graphQLErrors?.[0],
          ),
        )
        .find((m): m is string => Boolean(m));
      toast.enqueueToast(
        validationMessage ?? t('depositReview.couldNotCompleteAllDeposits'),
        { variant: 'error' },
      );
      return;
    }
    if (
      !custodianActionTakenRef.current &&
      !workflowCompletedRef.current &&
      !hasErrorRef.current
    ) {
      // Only complete workflow if no custodian action was taken and no error occurred.
      // Custodian actions handle completion in their onCompleted callbacks.
      // Using refs to prevent race conditions with React's async state updates
      await handleWorkflowCompletion('success');
    } else {
      await handleWorkflowCompletion('silent');
    }
  };

  const addAnotherDeposit = async () => {
    setLoading(true);
    const targetSubStep =
      findAddAnotherDepositTargetSubStep(workflowCompletion);

    await updateContextWithFields({
      ...toTransferContextData({}),
      currentDepositTemporaryId: undefined,
    });

    const { data: goToStepData } = await goToStep({
      variables: {
        input: {
          workflowCompletionId: workflowCompletion?.id,
          subStepId: targetSubStep?.id,
        },
      },
    });

    setActiveWorkflowCompletion(goToStepData.goToSubStep.workflowCompletion);
    setLoading(false);
  };

  return (
    <WorkflowCompletionForm
      onSubmit={submit}
      buttonFooter={
        canAddAnother && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              flex: 1,
            }}
          >
            <Button
              data-testid='deposit-review-add-another-button'
              variant='contained'
              sx={{ mt: 3, mr: 1 }}
              disabled={loading}
              onClick={addAnotherDeposit}
              startIcon={<Loop />}
            >
              {t('depositReview.addAnotherDeposit')}
            </Button>
          </Box>
        )
      }
      submitButtonProps={{
        label: t(
          resolveDepositReviewSubmitLabelKey(
            mode,
            applicableData.temporaryDeposits ?? [],
          ),
        ),
        disabled: loading,
        dataTestId: 'deposit-review-continue-button',
      }}
    >
      <Typography
        variant='displayLarge'
        sx={{ mt: 1, mb: options?.subtitle ? 0 : 2 }}
      >
        {translateBackendString(options?.title)}
      </Typography>
      {options?.subtitle && (
        <Typography variant='bodyLarge' sx={{ mb: 3 }}>
          <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
            {translateBackendString(options?.subtitle)}
          </ReactMarkdown>
        </Typography>
      )}
      <DepositReviewVisual
        options={options}
        dummyDeposit={dummyDeposit}
        setDummyDeposit={setDummyDeposit}
        workflowCompletion={workflowCompletion}
        disableInteractions={loading}
      />
    </WorkflowCompletionForm>
  );
};

export default DepositReview;
